import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { getWorkflowToken } from '@/api';
import { DOMAIN_HOST } from '@/api/lib/config';
import { AP_EMBED_URL } from '@/api/lib/config';
import { useTheme } from '@/context/ThemeContext';
import { useWorkflowTitle } from '@/context/WorkflowTitleContext';

interface ActivepiecesConfig {
    prefix?: string;
    instanceUrl: string;
    jwtToken: string;
    embedding: {
        containerId?: string;
        builder?: {
            disableNavigation?: boolean;
            hideLogo?: boolean;
            hideFlowName?: boolean;
            homeButtonClickedHandler?: () => void;
            homeButtonIcon?: 'logo' | 'back';
        };
        dashboard?: {
            hideSidebar?: boolean;
            hideFlowsPageNavbar?: boolean;
            hidePageHeader?: boolean;
        };
        hideFolders?: boolean;
        styling?: {
            fontUrl?: string;
            fontFamily?: string;
            mode?: 'light' | 'dark';
        };
        navigation?: {
            handler: ({ route }: { route: string }) => void;
        };
    };
}

declare global {
    interface Window {
        activepieces?: {
            configure: (config: ActivepiecesConfig) => Promise<void>;
            navigate: (params: { route: string }) => void;
            getMcpInfo: () => Promise<any>;
            getMcpTools: () => Promise<any>;
            addMcpTool: (params: { 
                pieceName: string; 
                mcpId: string;
                connectionId?: string; 
                status?: 'ENABLED' | 'DISABLED' 
            }) => Promise<any>;
            updateMcpTool: (params: { 
                pieceName: string; 
                mcpId: string;
                connectionId?: string; 
                status?: 'ENABLED' | 'DISABLED' 
            }) => Promise<any>;
            removeMcpTool: (params: { 
                pieceName: string;
                mcpId: string;
            }) => Promise<any>;
            connect: (params: {
                pieceName: string;
                connectionName?: string;
                newWindow?: { width?: number, height?: number, top?: number, left?: number };
            }) => Promise<{
                connection?: {
                    id: string;
                    name: string;
                }
            }>;
            request: <T = any>(params: {
                path: string;
                method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'OPTIONS' | 'PATCH' | 'HEAD';
                body?: Record<string, any>;
                queryParams?: Record<string, string | number | boolean>;
                headers?: Record<string, string>;
            }) => Promise<T>;
            extractActivepiecesRouteFromUrl?: (params: { vendorUrl: string }) => string;
        };
    }
}

interface ActivepiecesEmbedProps {
    /** E.g. "/flows", "/runs", "/connections" */
    route: string;
    onInternalRouteChange?: (route: string) => void;
    onConfigured?: () => void;
}

/**
 * Embeds Activepieces in an iframe, controlling only the top-level route.
 * Any sub-route changes (flow ID, run ID) remain internal to the iframe.
 */
export const ActivepiecesEmbed: React.FC<ActivepiecesEmbedProps> = React.memo(({
    route,
    onInternalRouteChange,
    onConfigured
}) => {
    const [loading, setLoading] = useState(true);
    const [configured, setConfigured] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const configuredRef = useRef(false);
    const isConfiguringRef = useRef(false); // Track if configuration is in progress
    const navigate = useNavigate();
    const isNavigatingRef = useRef(false);
    // Track the last route that was set by the navigation handler to avoid loops
    const lastInternalRouteRef = useRef<string>('');
    // Track the intended target route to ignore iframe's default navigation - set immediately
    const targetRouteRef = useRef<string>(route);
    // Store the last query params for each base route to preserve filters
    const lastQueryParamsRef = useRef<Record<string, string>>({});
    const { theme } = useTheme();
    const isFirstRenderRef = useRef(true); // Track if this is the first render
    const { setWorkflowTitle } = useWorkflowTitle(); // For dynamic page titles
    const [currentInternalRoute, setCurrentInternalRoute] = useState<string>(''); // Track the full internal route for title fetching

    // Helper to resolve theme (system -> light/dark)
    const getResolvedTheme = useCallback((): 'light' | 'dark' => {
        if (theme === 'system') {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return theme as 'light' | 'dark';
    }, [theme]);

    // Initialize with resolved theme
    const initialResolvedTheme = theme === 'system' 
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : theme as 'light' | 'dark';
    const initialThemeRef = useRef(initialResolvedTheme); // Track initial RESOLVED theme

    // Create configuration object - used for both initial config and theme sync
    const createActivepiecesConfig = useCallback((token: string, themeMode: 'light' | 'dark'): ActivepiecesConfig => {
        return {
            instanceUrl: `https://workflow.${DOMAIN_HOST}`,
            jwtToken: token,
            embedding: {
                containerId: 'activepieces-container',
                builder: {
                    hideLogo: true,
                    homeButtonClickedHandler: () => {
                        const currentPath = window.location.pathname;
                        if (currentPath.includes('/flows/')) {
                            window.activepieces?.navigate({ route: '/flows' });
                        } else if (currentPath.includes('/runs/')) {
                            window.activepieces?.navigate({ route: '/runs' });
                        } else if (currentPath.includes('/tables/')) {
                            window.activepieces?.navigate({ route: '/tables' });
                        } else if (currentPath.includes('/todos/')) {
                            window.activepieces?.navigate({ route: '/todos' });
                        } else {
                            navigate('/dashboard/workflows');
                        }
                    },
                    homeButtonIcon: 'back',
                },
                dashboard: {
                    hideSidebar: true,
                    hideFlowsPageNavbar: false,
                    hidePageHeader: false,
                },
                styling: {
                    fontUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap',
                    fontFamily: 'Inter',
                    mode: themeMode
                },
                navigation: {
                    handler: ({ route: internalRoute }: { route: string }) => {
                        // Update the current internal route for title fetching
                        setCurrentInternalRoute(internalRoute);
                        
                        // Ignore iframe's default /flows navigation if we're targeting a different route
                        if (internalRoute === '/flows' && targetRouteRef.current && targetRouteRef.current !== '/flows') {
                            return;
                        }
                        
                        // Allow query param updates even during programmatic navigation
                        const basePath = internalRoute.split('?')[0];
                        const queryString = internalRoute.includes('?') ? internalRoute.split('?')[1] : '';
                        
                        // If we're navigating and this is just a query param update for the same base path, allow it
                        const currentBasePath = lastInternalRouteRef.current.split('?')[0];
                        if (isNavigatingRef.current && basePath === currentBasePath && queryString) {
                            // Continue processing to update URL with query params
                        } else if (isNavigatingRef.current) {
                            onInternalRouteChange?.(internalRoute);
                            return;
                        }
                        
                        if (queryString) {
                            lastQueryParamsRef.current[basePath] = queryString;
                        }
                        
                        let finalRoute = internalRoute;
                        if (!queryString && lastQueryParamsRef.current[basePath]) {
                            finalRoute = `${basePath}?${lastQueryParamsRef.current[basePath]}`;
                            isNavigatingRef.current = true;
                            setTimeout(() => {
                                if (window.activepieces) {
                                    window.activepieces.navigate({ route: finalRoute });
                                }
                                setTimeout(() => {
                                    isNavigatingRef.current = false;
                                }, 100);
                            }, 0);
                        }
                        
                        lastInternalRouteRef.current = finalRoute;
                        
                        const fullPath = `/dashboard/workflows${finalRoute}`;
                        const currentFullPath = `${window.location.pathname}${window.location.search}`;
                        
                        if (currentFullPath !== fullPath) {
                            window.history.replaceState({}, "", fullPath);
                        }
                        
                        onInternalRouteChange?.(finalRoute);
                    }
                }
            }
        };
    }, [navigate, onInternalRouteChange]);

    // Load SDK and configure once
    useEffect(() => {
        let scriptEl: HTMLScriptElement | null = null;
        let isMounted = true;

        const configureOnce = async () => {
            // Atomic check-and-set to prevent race condition
            if (configuredRef.current || configured) {
                return;
            }
            
            // Check if currently configuring - set flag atomically
            if (isConfiguringRef.current) {
                return;
            }
            
            // Set flags immediately (synchronously) before any async operations
            isConfiguringRef.current = true;
            configuredRef.current = true;

            try {
                const tokenResp = await getWorkflowToken();
                if (!isMounted) return; // Component unmounted during async operation
                
                const config = createActivepiecesConfig(tokenResp.data ?? '', getResolvedTheme());
                await window.activepieces?.configure(config);
                
                if (!isMounted) return; // Component unmounted during async operation

                setConfigured(true);
                setLoading(false);
                onConfigured?.();
                
                // Don't navigate here - let the route effect handle it
            } catch {
                if (!isMounted) return;
                setError('Failed to configure Activepieces.');
                setLoading(false);
            } finally {
                isConfiguringRef.current = false; // Reset flag when done
            }
        };

        if (window.activepieces) {
            configureOnce();
        } else {
            scriptEl = document.createElement('script');
            scriptEl.src = AP_EMBED_URL;
            scriptEl.async = false;
            scriptEl.onload = () => configureOnce();
            scriptEl.onerror = () => {
                if (isMounted) {
                    setError('Failed to load Activepieces SDK script.');
                    setLoading(false);
                }
            };
            document.body.appendChild(scriptEl);
        }

        return () => {
            isMounted = false;
            // Don't reset configuredRef to handle Strict Mode double mount
            // Only remove script if it was added in this effect
            if (scriptEl?.parentNode) {
                scriptEl.parentNode.removeChild(scriptEl);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Theme synchronization - navigate back to workflows landing page on theme change
    useEffect(() => {
        // Skip on first render
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }

        // Skip if not configured yet
        if (!configured) {
            return;
        }

        // Check if theme actually changed
        const currentResolvedTheme = getResolvedTheme();
        const previousResolvedTheme = initialThemeRef.current;

        if (currentResolvedTheme === previousResolvedTheme) {
            return;
        }

        // Update the initial theme ref
        initialThemeRef.current = currentResolvedTheme;

        // Navigate back to workflows landing page instead of trying to remount iframe
        // This provides a cleaner experience and avoids complex state management
        navigate('/dashboard/workflows');

    }, [theme, configured, getResolvedTheme, navigate]);

    // Handle OS theme changes when in 'system' mode
    useEffect(() => {
        if (theme !== 'system' || !configured) {
            return;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleSystemThemeChange = () => {
            // Update theme ref and navigate back
            initialThemeRef.current = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            navigate('/dashboard/workflows');
        };
        
        mediaQuery.addEventListener('change', handleSystemThemeChange);
        return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
    }, [theme, configured, navigate]);

    // Browser back/forward button support
    useEffect(() => {
        const handlePopState = () => {
            if (!configured || !window.activepieces) {
                return;
            }
            
            // Check if this popstate was triggered by our own replaceState
            // If so, ignore it (it's an internal iframe navigation)
            if (isNavigatingRef.current) {
                return;
            }
            
            // Small delay to ensure URL has updated
            setTimeout(() => {
                const currentPath = window.location.pathname;
                const currentSearch = window.location.search;
                
                // Extract Activepieces route from current URL
                let workflowRoute = currentPath.replace('/dashboard/workflows', '');
                if (!workflowRoute) workflowRoute = '/';
                if (currentSearch) workflowRoute += currentSearch;
                
                // Set flag to prevent navigation handler from triggering
                isNavigatingRef.current = true;
                
                // Navigate iframe to match browser URL
                if (window.activepieces) {
                    window.activepieces.navigate({ route: workflowRoute });
                }
                
                // Reset flag
                setTimeout(() => {
                    isNavigatingRef.current = false;
                }, 100);
            }, 50);
        };
        
        if (configured) {
            window.addEventListener("popstate", handlePopState);
            return () => window.removeEventListener("popstate", handlePopState);
        }
    }, [configured]);

    // Fetch and update workflow title when internal route changes
    useEffect(() => {
        const routeToCheck = currentInternalRoute || route;
        
        if (!configured || !window.activepieces?.request) {
            return;
        }

        const fetchWorkflowTitle = async () => {
            if (!window.activepieces?.request) return;
            
            try {
                // Extract flow ID from route
                const flowMatch = routeToCheck.match(/^\/flows\/([^/?]+)/);
                const runMatch = routeToCheck.match(/^\/runs\/([^/?]+)/);
                
                if (flowMatch) {
                    const flowId = flowMatch[1];
                    
                    const flowData = await window.activepieces.request<{
                        id: string;
                        displayName: string;
                        version: { displayName: string };
                    }>({
                        method: 'GET',
                        path: `/flows/${flowId}`,
                    });
                    
                    if (flowData?.version?.displayName) {
                        setWorkflowTitle(flowData.version.displayName);
                    } else if (flowData?.displayName) {
                        setWorkflowTitle(flowData.displayName);
                    } else {
                        setWorkflowTitle(null);
                    }
                } else if (runMatch) {
                    const runId = runMatch[1];
                    
                    const runData = await window.activepieces.request<{
                        id: string;
                        flowDisplayName: string;
                    }>({
                        method: 'GET',
                        path: `/flows/runs/${runId}`,
                    });
                    
                    if (runData?.flowDisplayName) {
                        setWorkflowTitle(runData.flowDisplayName);
                    } else {
                        setWorkflowTitle(null);
                    }
                } else {
                    // Not a specific flow/run page, clear title
                    setWorkflowTitle(null);
                }
            } catch (error) {
                console.error('Failed to fetch workflow title:', error);
                setWorkflowTitle(null);
            }
        };

        // Small delay to ensure route is stable
        const timer = setTimeout(fetchWorkflowTitle, 200);
        return () => clearTimeout(timer);
    }, [currentInternalRoute, route, configured, setWorkflowTitle]);

    // Handle route prop changes
    useEffect(() => {
        if (configured && window.activepieces && route) {
            // Only navigate if the route is different from what the iframe already has
            // This prevents re-navigation when the URL changes due to iframe's own navigation
            if (route !== lastInternalRouteRef.current) {
                // Check if this is the first navigation (iframe just configured)
                const isInitialNavigation = lastInternalRouteRef.current === '';
                
                // Set target route to ignore iframe's default navigation
                targetRouteRef.current = route;
                
                // Set flag to indicate we're programmatically navigating
                isNavigatingRef.current = true;
                lastInternalRouteRef.current = route;
                
                setTimeout(() => {
                    if (window.activepieces) {
                        window.activepieces.navigate({ route });
                    }
                    // Reset flag and target after navigation
                    setTimeout(() => {
                        isNavigatingRef.current = false;
                        targetRouteRef.current = '';
                    }, 150);
                }, isInitialNavigation ? 100 : 0);
            }
        }
    }, [route, configured]);

    // Memoize skeleton components since they're static
    const FlowsSkeleton = React.memo(() => (
        <div className="h-full flex flex-col bg-background">
            <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Skeleton className="h-6 w-20" />
                </div>
                <div className="flex items-center gap-2">
                    <Skeleton className="h-9 w-32 rounded-md" />
                    <Skeleton className="h-9 w-28 rounded-md" />
                </div>
            </div>
            <div className="flex-1 p-4">
                {[...Array(3)].map((_, i) => (
                    <div
                        key={i}
                        className="mb-3 p-3 border border-border rounded-lg flex items-center gap-4 bg-card"
                    >
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-20" />
                    </div>
                ))}
            </div>
        </div>
    ));

    const RunsSkeleton = React.memo(() => (
        <div className="h-full flex flex-col bg-background">
            <div className="p-4 border-b border-border flex items-center justify-between">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-9 w-28 rounded-md" />
            </div>
            <div className="flex-1 p-4">
                {[...Array(4)].map((_, i) => (
                    <div
                        key={i}
                        className="mb-3 p-3 border border-border rounded-lg flex items-center gap-4 bg-card"
                    >
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-20" />
                    </div>
                ))}
            </div>
        </div>
    ));

    const ConnectionsSkeleton = React.memo(() => (
        <div className="h-full flex flex-col bg-background">
            <div className="p-4 border-b border-border flex items-center justify-between">
                <Skeleton className="h-6 w-28" />
                <Skeleton className="h-9 w-36 rounded-md" />
            </div>
            <div className="flex-1 p-4">
                {[...Array(4)].map((_, i) => (
                    <div
                        key={i}
                        className="mb-3 p-3 border border-border rounded-lg flex items-center gap-4 bg-card"
                    >
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-20" />
                    </div>
                ))}
            </div>
        </div>
    ));

    // Render appropriate skeleton based on route
    const renderSkeleton = () => {
        if (route.includes('/connections')) return <ConnectionsSkeleton />;
        if (route.includes('/runs')) return <RunsSkeleton />;
        return <FlowsSkeleton />;
    };

    return (
        <div className="relative w-full h-full bg-background">
            <div id="activepieces-container" className="w-full h-full bg-background" />

            <AnimatePresence>
                {loading && !error && (
                    <motion.div
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="absolute inset-0 bg-background overflow-hidden"
                    >
                        {renderSkeleton()}
                    </motion.div>
                )}
            </AnimatePresence>

            {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-background">
                    <div className="text-destructive font-semibold">{error}</div>
                </div>
            )}

            <style>{`
        #activepieces-container iframe {
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 0;
          background: transparent;
        }
      `}</style>
        </div>
    );
});